import { 
  doc, 
  collection, 
  updateDoc, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  limit,
  Firestore 
} from 'firebase/firestore';
import { db } from './firebase';

// Types for analytics data
export interface AdAnalytics {
  id?: string;
  adSpotId: string;
  date: string; // YYYY-MM-DD format for daily aggregation
  impressions: number;
  clicks: number;
  ctr: number; // Click-through rate percentage
  location: string; // 'home-hero-banner', 'market-hub-sponsored', 'article-footer', 'carousel-sponsored', etc
  
  // User context (optional)
  userAgent?: string;
  referrer?: string;
  sessionDuration?: number;
  
  // Metadata
  createdAt: string;
  updatedAt: string;
}

export interface DailyMetrics {
  date: string;
  totalImpressions: number;
  totalClicks: number;
  totalCtr: number;
  locationBreakdown: {
    location: string;
    impressions: number;
    clicks: number;
    ctr: number;
  }[];
}

export interface DateRange {
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
}

export class AnalyticsService {
  private static instance: AnalyticsService;
  private readonly COLLECTION_NAME = 'ad_analytics';
  private readonly DAILY_METRICS_COLLECTION = 'daily_ad_metrics';

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  private getDb(): Firestore {
    if (!db) {
      throw new Error('Firebase not initialized');
    }
    return db;
  }

  // Get today's date in YYYY-MM-DD format
  private getTodayString(): string {
    return new Date().toISOString().split('T')[0];
  }

  // Track detailed analytics event
  async trackAnalyticsEvent(data: {
    adSpotId: string;
    location: string;
    eventType: 'impression' | 'click';
    userAgent?: string;
    referrer?: string;
  }): Promise<void> {
    try {
      // Skip analytics during build/SSR phase
      if (typeof window === 'undefined') {
        return;
      }
      
      const database = this.getDb();
      if (!database) {
        return; // No database connection available
      }
      
      const today = this.getTodayString();
      
      // Create or update daily analytics record
      const analyticsData: Omit<AdAnalytics, 'id'> = {
        adSpotId: data.adSpotId,
        date: today,
        impressions: data.eventType === 'impression' ? 1 : 0,
        clicks: data.eventType === 'click' ? 1 : 0,
        ctr: 0, // Will be calculated later
        location: data.location,
        userAgent: data.userAgent,
        referrer: data.referrer,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // For now, just create individual events (Phase 1)
      // In Phase 2, we can aggregate to daily records
      await addDoc(collection(database, this.COLLECTION_NAME), analyticsData);
      
      // Update daily metrics aggregation
      await this.updateDailyMetrics(data.adSpotId, data.location, data.eventType);
      
    } catch (error) {
      // Silent fail for analytics - don't break user experience
      console.warn('Analytics tracking failed:', error);
    }
  }

  // Update daily aggregated metrics
  private async updateDailyMetrics(
    adSpotId: string, 
    location: string, 
    eventType: 'impression' | 'click'
  ): Promise<void> {
    try {
      // Skip analytics during build/SSR phase
      if (typeof window === 'undefined') {
        return;
      }
      
      const database = this.getDb();
      if (!database) {
        return; // No database connection available
      }
      
      const today = this.getTodayString();
      const metricsId = `${today}-${location}`;
      
      // For Phase 1, we'll use a simple document per day-location
      const docRef = doc(database, this.DAILY_METRICS_COLLECTION, metricsId);
      
      // Get existing metrics for today
      const existingMetrics = await this.getDailyMetrics(today, location);
      
      const updatedMetrics: DailyMetrics = {
        date: today,
        totalImpressions: existingMetrics?.totalImpressions || 0,
        totalClicks: existingMetrics?.totalClicks || 0,
        totalCtr: 0,
        locationBreakdown: existingMetrics?.locationBreakdown || []
      };

      // Update counts
      if (eventType === 'impression') {
        updatedMetrics.totalImpressions += 1;
      } else {
        updatedMetrics.totalClicks += 1;
      }

      // Recalculate CTR
      updatedMetrics.totalCtr = updatedMetrics.totalImpressions > 0 
        ? (updatedMetrics.totalClicks / updatedMetrics.totalImpressions) * 100 
        : 0;

      // Update location breakdown
      const locationIndex = updatedMetrics.locationBreakdown.findIndex(l => l.location === location);
      if (locationIndex >= 0) {
        if (eventType === 'impression') {
          updatedMetrics.locationBreakdown[locationIndex].impressions += 1;
        } else {
          updatedMetrics.locationBreakdown[locationIndex].clicks += 1;
        }
        updatedMetrics.locationBreakdown[locationIndex].ctr = 
          updatedMetrics.locationBreakdown[locationIndex].impressions > 0
            ? (updatedMetrics.locationBreakdown[locationIndex].clicks / updatedMetrics.locationBreakdown[locationIndex].impressions) * 100
            : 0;
      } else {
        updatedMetrics.locationBreakdown.push({
          location,
          impressions: eventType === 'impression' ? 1 : 0,
          clicks: eventType === 'click' ? 1 : 0,
          ctr: 0
        });
      }

      await updateDoc(docRef, updatedMetrics as any);
    } catch (error) {
      // Silent fail for aggregation
      console.warn('Daily metrics update failed:', error);
    }
  }

  // Get daily metrics for a specific date and location
  async getDailyMetrics(date: string, location?: string): Promise<DailyMetrics | null> {
    try {
      const database = this.getDb();
      
      if (location) {
        const metricsId = `${date}-${location}`;
        const docRef = doc(database, this.DAILY_METRICS_COLLECTION, metricsId);
        const docSnap = await getDocs(query(collection(database, this.DAILY_METRICS_COLLECTION), where('__name__', '==', metricsId)));
        
        if (!docSnap.empty) {
          return docSnap.docs[0].data() as DailyMetrics;
        }
      }
      
      return null;
    } catch (error) {
      console.warn('Failed to get daily metrics:', error);
      return null;
    }
  }

  // Get analytics for date range
  async getAnalyticsData(dateRange: DateRange, location?: string): Promise<AdAnalytics[]> {
    try {
      const database = this.getDb();
      let q = query(
        collection(database, this.COLLECTION_NAME),
        where('date', '>=', dateRange.startDate),
        where('date', '<=', dateRange.endDate),
        orderBy('date', 'desc'),
        limit(1000) // Limit for performance
      );

      if (location) {
        q = query(q, where('location', '==', location));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as AdAnalytics));
    } catch (error) {
      console.warn('Failed to get analytics data:', error);
      return [];
    }
  }

  // Get real-time metrics (last 24 hours)
  async getRealtimeMetrics(): Promise<{
    last24Hours: DailyMetrics;
    last7Days: DailyMetrics[];
  }> {
    try {
      const today = this.getTodayString();
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const weekAgoString = weekAgo.toISOString().split('T')[0];

      const last7DaysData = await this.getAnalyticsData({
        startDate: weekAgoString,
        endDate: today
      });

      // Aggregate last 24 hours
      const last24Hours: DailyMetrics = {
        date: today,
        totalImpressions: 0,
        totalClicks: 0,
        totalCtr: 0,
        locationBreakdown: []
      };

      const todayData = last7DaysData.filter(d => d.date === today);
      last24Hours.totalImpressions = todayData.reduce((sum, d) => sum + d.impressions, 0);
      last24Hours.totalClicks = todayData.reduce((sum, d) => sum + d.clicks, 0);
      last24Hours.totalCtr = last24Hours.totalImpressions > 0 
        ? (last24Hours.totalClicks / last24Hours.totalImpressions) * 100 
        : 0;

      // Group by location for breakdown
      const locationMap = new Map<string, { impressions: number; clicks: number }>();
      todayData.forEach(d => {
        const existing = locationMap.get(d.location) || { impressions: 0, clicks: 0 };
        locationMap.set(d.location, {
          impressions: existing.impressions + d.impressions,
          clicks: existing.clicks + d.clicks
        });
      });

      last24Hours.locationBreakdown = Array.from(locationMap.entries()).map(([location, data]) => ({
        location,
        impressions: data.impressions,
        clicks: data.clicks,
        ctr: data.impressions > 0 ? (data.clicks / data.impressions) * 100 : 0
      }));

      // Process last 7 days (simplified for Phase 1)
      const last7Days: DailyMetrics[] = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateString = date.toISOString().split('T')[0];
        
        const dayData = last7DaysData.filter(d => d.date === dateString);
        last7Days.push({
          date: dateString,
          totalImpressions: dayData.reduce((sum, d) => sum + d.impressions, 0),
          totalClicks: dayData.reduce((sum, d) => sum + d.clicks, 0),
          totalCtr: 0, // Calculate if needed
          locationBreakdown: []
        });
      }

      return { last24Hours, last7Days };
    } catch (error) {
      console.warn('Failed to get realtime metrics:', error);
      return {
        last24Hours: { date: this.getTodayString(), totalImpressions: 0, totalClicks: 0, totalCtr: 0, locationBreakdown: [] },
        last7Days: []
      };
    }
  }
}

// Export singleton instance
export const analyticsService = AnalyticsService.getInstance();
